import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageCircle, FiX, FiUser } = FiIcons;

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [showWelcomeTooltip, setShowWelcomeTooltip] = useState(false);

  const toggleChat = () => {
    if (!hasBeenOpened && !isOpen) {
      setHasBeenOpened(true);
    }
    setIsOpen(!isOpen);
    setShowWelcomeTooltip(false);
  };

  // Show welcome tooltip after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasBeenOpened && !isOpen) {
        setShowWelcomeTooltip(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [hasBeenOpened, isOpen]);

  // Hide welcome tooltip after some time
  useEffect(() => {
    if (showWelcomeTooltip) {
      const timer = setTimeout(() => {
        setShowWelcomeTooltip(false);
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [showWelcomeTooltip]);

  return (
    <>
      {/* Chat Container - Always mounted after first open, just hidden/shown */}
      {hasBeenOpened && (
        <>
          {/* Backdrop for mobile */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                onClick={() => setIsOpen(false)}
              />
            )}
          </AnimatePresence>

          {/* Chat Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ 
              opacity: isOpen ? 1 : 0, 
              scale: isOpen ? 1 : 0.8, 
              y: isOpen ? 0 : 20,
              pointerEvents: isOpen ? 'auto' : 'none'
            }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="fixed bottom-24 right-4 md:right-6 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
            style={{ 
              width: '90vw', 
              maxWidth: '400px', 
              height: '500px'
            }}
          >
            {/* Header */}
            <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <SafeIcon icon={FiUser} className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">Insurance Specialist</h3>
                  <p className="text-xs text-blue-100">Ask me about business insurance</p>
                </div>
              </div>
              <button
                onClick={toggleChat}
                className="text-white hover:bg-blue-500 p-1 rounded-full transition-colors"
              >
                <SafeIcon icon={FiX} className="h-5 w-5" />
              </button>
            </div>

            {/* Chat Content - iframe stays mounted */}
            <div className="h-full pb-16">
              <iframe
                src="https://studio.pickaxe.co/_embed/Business_Insurance_Specialist__Chat_Agent_O5EHE?d=deployment-5d294cce-f428-43f7-8630-d06a450d2982"
                className="w-full h-full border-0"
                allow="microphone"
                title="Virtual Insurance Agent"
              />
            </div>
          </motion.div>
        </>
      )}

      {/* Floating Chat Button */}
      <motion.button
        onClick={toggleChat}
        className="fixed bottom-6 right-4 md:right-6 z-40 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, duration: 0.5, ease: "easeOut" }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon icon={FiMessageCircle} className="h-6 w-6" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Notification Dot - only show when never opened and chat is closed */}
        {!hasBeenOpened && !isOpen && (
          <motion.div
            className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        )}
      </motion.button>

      {/* Welcome Tooltip - only show when never opened */}
      <AnimatePresence>
        {showWelcomeTooltip && !hasBeenOpened && !isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.5 }}
            className="fixed bottom-20 right-4 md:right-6 z-30 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg max-w-xs"
          >
            <div className="text-sm">
              <p className="font-semibold mb-1">Need help with insurance?</p>
              <p className="text-gray-300">Chat with our virtual agent!</p>
            </div>
            <div className="absolute bottom-0 right-4 w-0 h-0 border-l-4 border-l-transparent border-r-4 border-r-transparent border-t-4 border-t-gray-900 transform translate-y-full" />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;